import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { pushTrackingEvent } from '@theme/tracking';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('@theme/slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('@theme/quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    // Registered before navigateToProduct so the tracking push always runs
    // before that handler's window.location.href assignment can interrupt
    // this tick's remaining listeners.
    this.addEventListener('click', this.#trackTrustScoreClick);
    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('click', this.#trackTrustScoreClick);
  }

  /**
   * Fires a GTM dataLayer event when a click lands on the TrustScore badge.
   *
   * The inert badge (.product-card__trust-score-stack) is `pointer-events:
   * none` so that clicking it still navigates to the product like the rest
   * of the card — event.target is therefore whatever sits behind it, never
   * the badge itself. We detect a "click on TrustScore" by geometry instead:
   * did the click point fall inside the badge's rendered bounds. The same
   * test covers the breakdown-trigger variant, which does take the pointer.
   * @param {Event} event
   */
  #trackTrustScoreClick = (event) => {
    if (!(event instanceof MouseEvent)) return;

    const badge = this.querySelector('.product-card__trust-score-stack');
    if (!badge) return;

    const rect = badge.getBoundingClientRect();
    const withinBounds =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!withinBounds) return;

    // Queued rather than pushed bare: an inert badge's click navigates in this
    // same tick, and a raw push does not survive that unless GTM is already
    // loaded. See `pushTrackingEvent`.
    pushTrackingEvent({
      event: 'trust_score_click',
      trust_score: this.dataset.trustScore || null,
      product_id: this.dataset.productId || null,
      // What the click did. With the `product_card_trust_breakdown` theme
      // setting on, the badge is a button that opens the breakdown in place
      // (snippets/plp-trust-breakdown.liquid, which fires its own
      // `trust_breakdown_open`); off, the click falls through to the card link.
      action: badge.hasAttribute('data-trust-breakdown-url') ? 'breakdown' : 'product',
    });
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const next = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!next) return;

    next.removeAttribute('loading');

    // `sizes="auto,…"` is only valid on a lazy image (card-gallery.liquid pairs
    // the two deliberately). Dropping `loading` here without dropping `auto`
    // invalidates the whole attribute, so the browser falls back to 100vw and
    // preloads a candidate about twice as wide as the slot — measured on a PDP
    // card row as 1118px files for a 256px slot, ~61KB per image against ~38KB
    // once the prefix is removed. Preload the image, not a larger one.
    const sizes = next.getAttribute('sizes');
    if (sizes?.startsWith('auto,')) next.setAttribute('sizes', sizes.slice('auto,'.length).trim());
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);

    // No `quickAdd.fetchProductPage` here. It used to follow every variant update, and it was a
    // second full product-page request on top of the one the variant picker had just made to
    // produce this event — for a response that was discarded (`fetchProductPage` returns the
    // parsed document without caching it; only `QuickAddComponent#handleClick` populates the
    // cache). At best it warmed the HTTP cache for a modal that may never open. The desktop
    // hover/focus prefetch (`#handleQuickAdd`) still runs, and the modal fetches on open.

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const productId = this.dataset.productId;
    const scope = productId ? `[data-product-id="${productId}"]` : '';
    const priceElement = this.querySelector(`product-price${scope}`);
    const newPriceElement = event.detail.data.html.querySelector(`product-price${scope}`);

    if (!priceElement || !newPriceElement) return;

    const priceContainer = priceElement.querySelector(`[ref='priceContainer']`);
    const newPriceContainer = newPriceElement.querySelector(`[ref='priceContainer']`);

    if (priceContainer && newPriceContainer) {
      morph(priceContainer, newPriceContainer);
    }

    // The notes (the first-order "Extra 10% off up to $75" line) render as a
    // SIBLING of the container rather than inside it — see the comment in
    // snippets/price.liquid for why — so morphing the container alone leaves the
    // previous variant's footnote sitting under the new price.
    //
    // A card cannot leave this to ProductPrice.updatePrice, which is what swaps
    // the notes everywhere else: the card's <product-price> carries no
    // data-block-id, so that method's lookup finds nothing and bails, and
    // #handleVariantUpdate stops the event before it can reach the section
    // listener it is bound to anyway.
    const priceNotes = priceElement.querySelector(`[ref='priceNotes']`);
    const newPriceNotes = newPriceElement.querySelector(`[ref='priceNotes']`);

    if (!newPriceNotes) {
      priceNotes?.remove();
    } else if (priceNotes) {
      morph(priceNotes, newPriceNotes);
    } else {
      // price.liquid emits the notes last inside <product-price>.
      priceElement.insertAdjacentElement('beforeend', /** @type {Element} */ (newPriceNotes.cloneNode(true)));
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const productId = this.dataset.productId;
    const responseProductCard = productId
      ? event.detail.data.html?.querySelector(`product-card[data-product-id="${productId}"]`)
      : event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const productId = this.dataset.productId;
    const picker = productId
      ? event.detail.data.html.querySelector(`swatches-variant-picker-component[data-product-id="${productId}"]`)
      : null;
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      picker?.querySelectorAll('input:checked') ?? event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * A scrollable row of variant thumbnails under a product card's image, letting a shopper
 * switch the tile between a family's prints/colours/scents without opening the product.
 *
 * Rendered by snippets/product-card-variant-strip.liquid, which replaced the old "5 prints"
 * chip. Read that snippet's doc block first: it explains why the thumbnails have to be
 * resolved server-side (these families are combined listings, so the siblings' photos live
 * on separate products and are NOT in the card's own slideshow).
 *
 * That is also why this does not reuse ProductCard's `previewVariant`. That method drives
 * the slideshow by slide id, and the images this switches between have no slide to select.
 * It swaps the `src` of the card's currently visible <img> instead, and puts the original
 * back when the card is reset.
 *
 * Deliberately not a fetch: switching the tile costs one image request (the 600px preview
 * named in the tile's `data-preview-src`) and no round trip to the Section Rendering API.
 * The price is knowingly left alone — see `#select`.
 */
class VariantStrip extends Component {
  /** @type {string | null} */
  #originalSrc = null;
  /** @type {string | null} */
  #originalSrcset = null;
  /** @type {string | null} */
  #originalSizes = null;
  /** @type {HTMLImageElement | null} */
  #targetImage = null;
  /**
   * The card's own price block as rendered, cloned before the first swap so clicking back to
   * the original print restores it exactly rather than approximating it.
   * @type {Node | null}
   */
  #originalPrice = null;
  /**
   * The card's own quick-add / wishlist values, captured before the first swap so returning
   * to its tile restores them.
   * @type {Record<string, string | undefined> | null}
   */
  #homeCartData = null;
  /**
   * Cached `#homeTile`. Re-resolved if the gallery re-rendered under us.
   * @type {Element | null}
   */
  #homeTileRef = null;
  /**
   * Viewport x of the interaction being handled, used to resolve a hit on the track's
   * padding to the tile nearest it. Read from the event rather than tracked on pointermove
   * so it is correct for a touch tap, which has no preceding move.
   * @type {number | null}
   */
  #lastPointerX = null;

  connectedCallback() {
    super.connectedCallback();
    this.#captureHomeTile();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointerover', this.#handlePointerOver);

    // The card moves its own slides for reasons that have nothing to do with this strip:
    // `show_second_image_on_hover` previews the next image on pointerenter, and a carousel
    // card has arrows. Because a selection is an in-place `src` swap on whichever <img> is
    // visible (see #select), those two mechanisms fight: the gallery would hand back a slide
    // still carrying a previewed print, or strand the shopper's chosen print behind a
    // hover-preview of the original.
    //
    // The shopper's click wins. On every slide change the borrowed <img> is handed back
    // untouched and the selection re-applied to whatever is now on screen, so the card keeps
    // showing the chosen print through hover, hover-out and the carousel arrows alike.
    this.#card?.addEventListener(SlideshowSelectEvent.eventName, this.#reapply);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointerover', this.#handlePointerOver);
    this.#card?.removeEventListener(SlideshowSelectEvent.eventName, this.#reapply);
  }

  /**
   * Re-pin the selected print onto the slide the gallery just moved to.
   *
   * The target is taken from the event itself (`detail.slide`), not from `#image`: this runs
   * from the slideshow's own select event, and `Slideshow#select` marks the incoming slide
   * `aria-hidden="false"` synchronously while the outgoing one is only cleared later by
   * `#updateVisibleSlides`. In between, BOTH slides read as visible and `#image` — first
   * match in DOM order — returns the outgoing slide on every forward move (carousel arrow,
   * hover-in), so the print was painted onto the slide being hidden while the one on screen
   * kept the original photo. Deferring to a microtask does not close that window either;
   * the aria state settles on scroll end, not on the next tick.
   *
   * Only an event whose slide sits in this card's own gallery is trusted; anything else
   * falls back to `#image`, resolved after the aria state has had a chance to settle.
   *
   * @param {Event & { detail?: { slide?: Element } }} event
   */
  #reapply = (event) => {
    const tile = this.#selectedTile;
    if (!tile) return;

    this.#restore();

    const image = this.#imageIn(event?.detail?.slide);
    if (image) {
      this.#applyTo(tile, image);
      return;
    }

    Promise.resolve().then(() => {
      if (!this.isConnected) return;
      const current = this.#selectedTile;
      if (current) this.#applyTo(current);
    });
  };

  /**
   * The <img> inside a given gallery slide, if that slide belongs to this card.
   *
   * @param {Element | undefined} slide
   * @returns {HTMLImageElement | null}
   */
  #imageIn(slide) {
    if (!(slide instanceof Element)) return null;

    const gallery = this.#card?.querySelector(':scope .card-gallery');
    if (!gallery || !gallery.contains(slide)) return null;

    const image = slide.querySelector('img');
    return image instanceof HTMLImageElement ? image : null;
  }

  /** @returns {HTMLElement | null} */
  get #selectedTile() {
    const tile = this.querySelector('.product-card__variant-tile[aria-current="true"]');
    // The card's own print is not a swap — there is nothing to re-apply or restore for it.
    if (!(tile instanceof HTMLElement) || tile === this.#homeTile) return null;
    return tile;
  }

  /**
   * The tile for the print the card itself was rendered with, captured once at connect
   * before any click can move `aria-current`.
   *
   * Emphatically NOT "the first tile": an option's values come in the family's own order,
   * which has nothing to do with which sibling the grid surfaced — the natural-rubber bath
   * toys list Fish, Whale, Ducky, Seahorse while the card is the Seahorse. The strip marks
   * the right one server-side (see product-card-variant-strip.liquid); this reads that mark
   * rather than assuming a position.
   *
   * @returns {Element | null}
   */
  get #homeTile() {
    if (this.#homeTileRef?.isConnected) return this.#homeTileRef;

    // Read the server's own mark, which `#captureHomeTile` copied to a data- attribute at
    // connect. `[aria-current="true"]` must NOT be used here: it tracks the shopper's
    // current selection and has already moved by the time a click handler asks, so the
    // clicked tile would identify as home and every swap would no-op.
    this.#homeTileRef =
      this.querySelector('.product-card__variant-tile[data-home="true"]') ??
      this.querySelector('.product-card__variant-tile');
    return this.#homeTileRef;
  }

  /**
   * Stamp the tile the card was rendered with, before any interaction can move
   * `aria-current` off it.
   */
  #captureHomeTile() {
    if (this.querySelector('.product-card__variant-tile[data-home]')) return;
    const home =
      this.querySelector('.product-card__variant-tile[aria-current="true"]') ??
      this.querySelector('.product-card__variant-tile');
    if (home instanceof HTMLElement) home.dataset.home = 'true';
  }

  /**
   * Put the card's own image back exactly as it was rendered.
   *
   * Only the attributes this took away are restored, and only onto the element they were
   * taken from — a card whose gallery has since re-rendered (Section Rendering after a
   * filter change, say) leaves a stale reference here that must not be written to, which is
   * why the element identity is re-checked rather than assumed.
   */
  #restore = () => {
    const image = this.#targetImage;
    if (!image || !image.isConnected) {
      this.#targetImage = null;
      return;
    }

    if (this.#originalSrc) image.src = this.#originalSrc;
    if (this.#originalSrcset) image.setAttribute('srcset', this.#originalSrcset);
    if (this.#originalSizes) image.setAttribute('sizes', this.#originalSizes);

    this.#targetImage = null;
    this.#originalSrc = null;
    this.#originalSrcset = null;
    this.#originalSizes = null;
  };

  /** @returns {HTMLElement | null} */
  get #card() {
    return this.closest('product-card');
  }

  /**
   * The <img> the card is currently showing.
   *
   * Resolved per interaction rather than cached at connect time: the card's gallery is a
   * slideshow whose visible slide changes under us (hover-to-preview-second-image, and the
   * arrows on a carousel card), so the element that was visible at connect is often not the
   * one on screen by the time a thumbnail is clicked.
   */
  get #image() {
    const card = this.#card;
    if (!card) return null;

    // Anchored under the card's own `.card-gallery`. `querySelector` matches the selector
    // against the whole document and only filters the RESULT to descendants, so a bare
    // `slideshow-slide[aria-hidden="false"] img` is satisfied by an ancestor slide — and the
    // PDP's "More Like This" carousel wraps each CARD in one. There the first <img> in the
    // card is the trust-leaf icon in the corner row, which is what got borrowed: the leaf
    // turned into the print while the product photo never changed.
    const visibleSlide = card.querySelector(':scope .card-gallery slideshow-slide[aria-hidden="false"] img');
    if (visibleSlide instanceof HTMLImageElement) return visibleSlide;

    const anyImage = card.querySelector(':scope .card-gallery img');
    return anyImage instanceof HTMLImageElement ? anyImage : null;
  }

  /**
   * Record where the interaction actually landed, for `#nearestTile`.
   *
   * Touch needs this: the click a tap synthesises can carry clientX 0, while the pointerdown
   * that precedes it always carries the true coordinate. Without it a tap in the track's
   * padding resolves to no tile, the handler bails, and the card's own navigation takes the
   * shopper to the product page instead of switching the print.
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.clientX > 0) this.#lastPointerX = event.clientX;
  };

  /**
   * Hovering a tile warms the full-size image so the click that follows paints immediately.
   *
   * `mouse` only: on touch there is no hover to precede the tap, and firing this from a
   * touch-generated pointerover would fetch a full-size image for every tile a finger
   * happens to cross while scrolling the row.
   *
   * @param {PointerEvent} event
   */
  #handlePointerOver = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#lastPointerX = event.clientX;
    const tile = this.#tileFrom(event.target);
    const src = tile?.dataset.previewSrc;
    if (!src) return;

    // A bare Image() is enough to put it in the HTTP cache; nothing is inserted into the DOM.
    const warm = new Image();
    warm.decoding = 'async';
    warm.src = src;
  };

  /**
   * The tile an interaction belongs to.
   *
   * A hit on the track's own padding is forwarded to the tile nearest it horizontally. The
   * tiles are 26-28px so that four fit under a narrow mobile card, which is well under the
   * ~44px a finger needs; the track carries the difference as block padding, and this is
   * what makes a tap in that band count as a tap on the tile. It cannot be done in CSS with
   * an overhanging pseudo-element, because the track is a scroll container and clips it.
   *
   * Only the track's padding forwards, never the card at large: `closest` still has to find
   * this strip, so a tap anywhere else on the card navigates exactly as it always did.
   *
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #tileFrom(target) {
    if (!(target instanceof Element)) return null;

    const tile = target.closest('.product-card__variant-tile');
    if (tile instanceof HTMLElement) return tile;

    // The "+N" overflow link lives inside the track too. It is a real navigation to the
    // product page, not padding around a tile: without this, a click on its left edge —
    // within 16px of the last tile — was forwarded to that tile and `preventDefault`ed,
    // and the shopper who wanted the full picker got a colour swap instead.
    if (target.closest('.product-card__variant-more')) return null;

    const track = target.closest('.product-card__variant-track');
    if (!track || !this.contains(track)) return null;
    return this.#nearestTile();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #nearestTile() {
    const point = this.#lastPointerX;
    if (point == null) return null;

    /** @type {HTMLElement | null} */
    let best = null;
    let bestDistance = Infinity;

    for (const candidate of this.querySelectorAll('.product-card__variant-tile')) {
      if (!(candidate instanceof HTMLElement)) continue;
      const rect = candidate.getBoundingClientRect();

      // Distance to the tile's horizontal span: 0 while the pointer is within it.
      const distance = Math.max(rect.left - point, 0, point - rect.right);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }

    // Past half a tile's width beyond the last one, the shopper was not aiming at a tile.
    return bestDistance <= 16 ? best : null;
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    // A click synthesised from a tap can report clientX 0, which would make `#nearestTile`
    // pick the wrong tile or none at all — and "none" means the card's own handler takes the
    // tap to the product page. `#handlePointerDown` recorded the real x just before this.
    if (event instanceof MouseEvent && event.clientX > 0) this.#lastPointerX = event.clientX;

    const tile = this.#tileFrom(event.target);
    if (!tile) return;

    // The whole card is a click-to-navigate surface (ProductCard#navigateToProduct). These
    // tiles are <button>s, which that handler already treats as interactive and ignores, but
    // stopping here as well keeps the strip working if that guard is ever narrowed.
    event.preventDefault();
    event.stopPropagation();

    this.#select(tile);
  };

  /**
   * @param {HTMLElement} tile
   */
  #select(tile) {
    for (const other of this.querySelectorAll('.product-card__variant-tile[aria-current]')) {
      other.removeAttribute('aria-current');
    }
    tile.setAttribute('aria-current', 'true');

    // Clicking back to the print the card shipped with is an un-select: hand the <img> back
    // rather than re-painting the same photo over it.
    if (tile === this.#homeTile) {
      this.#restore();
    } else {
      this.#applyTo(tile);
    }

    this.#retargetLinks(tile.dataset.variantUrl);
    this.#applyPrice(tile);
    this.#retargetCart(tile);
  }

  /**
   * Point quick-add and the wishlist heart at the print the shopper is looking at.
   *
   * Without this the card's controls keep acting on whichever sibling the grid happened to
   * surface: switching to Blueberry and hitting add-to-cart added Coconut.
   *
   * These families are combined listings, so a sibling is a different PRODUCT rather than
   * another variant of this one — which is why three things move, not one. Quick-add posts a
   * variant id through its hidden input; the wishlist keys on product id and handle.
   *
   * A tile whose cart data could not be resolved server-side (no sibling product, no variant)
   * carries none of these attributes, and both controls are then left exactly as rendered —
   * on the card's own product. Adding the wrong thing to a cart is far worse than a switch
   * that only moves the picture.
   *
   * @param {HTMLElement} tile
   */
  #retargetCart(tile) {
    const card = this.#card;
    if (!card) return;

    // Remember the card's own values before anything is swapped, so returning to its tile
    // puts the controls back rather than leaving them on the last print looked at. Captured
    // before the branch below, which reads it.
    if (!this.#homeCartData) this.#homeCartData = this.#readCartData(card);

    const data = tile === this.#homeTile ? this.#homeCartData : tile.dataset;
    if (!data) return;

    const variantId = data.variantId;
    const variantInput = card.querySelector('quick-add-component input[name="id"]');
    if (variantId && variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;

      // The input ships `disabled` for a sold-out variant, which is what keeps an
      // unavailable print out of the cart — so it has to track the new print too.
      const available = data.available !== 'false';
      variantInput.disabled = !available;
      card.querySelector('quick-add-component')?.toggleAttribute('data-variant-unavailable', !available);
    }

    const heart = card.querySelector('wishlist-toggle-button');
    if (heart instanceof HTMLElement && data.productId) {
      heart.dataset.productId = data.productId;
      if (data.productHandle) heart.dataset.productHandle = data.productHandle;

      // Titles are shared across a combined listing's siblings today (combining strips the
      // print suffix), so this is belt-and-braces rather than a live bug — but the label and
      // the toast both read from it, so they follow if a family ever carries per-sibling
      // titles.
      if (data.productTitle && data.productTitle !== heart.dataset.productTitle) {
        const label = heart.getAttribute('aria-label');
        const previous = heart.dataset.productTitle;
        heart.dataset.productTitle = data.productTitle;
        if (label && previous) {
          const next = label.replace(previous, data.productTitle);
          heart.setAttribute('aria-label', next);
          heart.querySelector('button')?.setAttribute('aria-label', next);
        }
      }

      // The heart renders filled/empty from the store at connect; after repointing it at a
      // different product it has to re-read, or it shows the previous print's state.
      heart.dispatchEvent(new CustomEvent('wishlist:resync', { bubbles: false }));
    }
  }

  /**
   * Snapshot of the card's own cart-facing values, shaped like a tile's dataset.
   * @param {Element} card
   * @returns {Record<string, string | undefined>}
   */
  #readCartData(card) {
    const input = card.querySelector('quick-add-component input[name="id"]');
    const heart = card.querySelector('wishlist-toggle-button');
    return {
      variantId: input instanceof HTMLInputElement ? input.value : undefined,
      available: input instanceof HTMLInputElement && input.disabled ? 'false' : 'true',
      productId: heart instanceof HTMLElement ? heart.dataset.productId : undefined,
      productHandle: heart instanceof HTMLElement ? heart.dataset.productHandle : undefined,
      productTitle: heart instanceof HTMLElement ? heart.dataset.productTitle : undefined,
    };
  }

  /**
   * Show the price belonging to the selected print.
   *
   * The markup is already on the page — snippets/product-card-variant-strip.liquid renders
   * each sibling's price into an inert <template> in its tile — so this is a DOM swap, not a
   * fetch: no network on click, nothing to race or fail offline, and no second
   * implementation of the sale rules that could quote a figure checkout will not honour.
   *
   * `morph` rather than `replaceWith`, matching ProductCard#updatePrice: it patches the
   * existing nodes in place, so an unchanged price (the common case across a colourway
   * family) touches nothing and never flickers.
   *
   * @param {HTMLElement} tile
   */
  #applyPrice(tile) {
    const container = this.#priceContainer;
    if (!container) return;

    // The card's own price, kept so clicking back to the first tile restores it exactly.
    if (!this.#originalPrice) this.#originalPrice = container.cloneNode(true);

    const template = tile.querySelector('template.product-card__variant-price');
    const replacement =
      tile === this.#homeTile
        ? this.#originalPrice
        : template instanceof HTMLTemplateElement
          ? template.content.querySelector('[ref="priceContainer"]')
          : null;

    if (!replacement) return;

    // Cloned every time: morph mutates the node it is given, and both the template's content
    // and the saved original have to survive being applied more than once.
    morph(container, replacement.cloneNode(true));
  }

  /** @returns {Element | null} */
  get #priceContainer() {
    const card = this.#card;
    if (!card) return null;
    return card.querySelector('product-price [ref="priceContainer"]');
  }

  /**
   * Paint a tile's print onto the card's currently visible image.
   * @param {HTMLElement} tile
   * @param {HTMLImageElement | null} [image] - The <img> to paint; defaults to the visible one.
   */
  #applyTo(tile, image = this.#image) {
    const src = tile.dataset.previewSrc;
    if (!src) return;

    if (!image) return;

    // Remember what this <img> was showing before it was borrowed, so #restore can hand it
    // back byte-identical to what the server rendered.
    if (this.#targetImage !== image) {
      this.#targetImage = image;
      this.#originalSrc = image.getAttribute('src');
      this.#originalSrcset = image.getAttribute('srcset');
      this.#originalSizes = image.getAttribute('sizes');
    }

    // `srcset` has to go, not just `src`: with both set the browser picks from the srcset and
    // the swap silently does nothing.
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
  }

  /**
   * Repoint the card's links at the previewed variant, so the shopper lands on the print
   * they were looking at rather than back on the one the grid happened to surface.
   *
   * Title, image and the card's full-surface overlay link are all updated, because a card
   * can be entered through any of them — and ProductCard#navigateToProduct reads its URL
   * from `refs.productCardLink`, which is that overlay.
   *
   * The PRICE is deliberately not touched. Getting it right for a combined listing means
   * fetching the sibling product, which is exactly the round trip this feature exists to
   * avoid; showing a stale price next to a switched image would be worse than showing the
   * family's surfaced price, which is what the card said before any tile was clicked.
   *
   * @param {string | undefined} url
   */
  #retargetLinks(url) {
    if (!url) return;

    const card = this.#card;
    if (!card) return;

    const links = card.querySelectorAll(
      'a.product-card__link, a[ref="productTitleLink"], a[ref="cardGalleryLink"]'
    );

    for (const link of links) {
      if (link instanceof HTMLAnchorElement) link.href = url;
    }
  }
}

if (!customElements.get('variant-strip')) {
  customElements.define('variant-strip', VariantStrip);
}
